/*
 * The publishing rule, client-side.
 *
 * The authoritative implementation is kb.ArticleDetail.IsLive in the database -
 * one place, as datamodel/README.md insists. This module does not re-implement
 * the reader-facing filter (the public API only ever reads kb.ArticlePublished,
 * so nothing unpublished can leak through it); it exists so the admin UI can
 * label a row, and so the server can compute the same label for a list response
 * without asking the database a second time.
 *
 *     Article.IsVisible = 1
 *       AND Article.PublishAtUtc <= now
 *       AND a revision with IsCurrent = 1 exists
 *
 * Three conditions, and they are AND-ed, so the report has to pick one to blame.
 * The order the gates are tested in is the order they are reported in, which is
 * why "hidden" wins over "scheduled": IsVisible is the flag an author toggles to
 * withdraw an article outright, and saying "scheduled" about a hidden article
 * would suggest it will appear by itself, which it will not.
 *
 * "Unwritten" is last because it is not an editorial decision at all - it is the
 * state of an article row that exists with nothing attached to it yet, which the
 * LEFT JOIN in kb.ArticleDetail is there to allow.
 *
 * "Withdrawn" sits just before it: no revision is current either, but revisions
 * exist - the text was published and taken back through unpublish-text. That IS
 * an editorial decision, and until 2026-09-21 it reported as "unwritten" on every
 * screen (W6). Telling the two apart needs one more fact than the three gates,
 * the latest revision number, which is why PublishingGates carries it optionally:
 * a caller that cannot supply it gets the old answer.
 *
 * Release notes (S42, 2026-10-12) are published by the same rule through the
 * same states, stated once for them in kb.ReleaseNoteDetail.IsLive, so the gate
 * functions here serve them unchanged. Only the two describe functions name the
 * thing, and they take it as a `noun` whose default is 'article'.
 */
/**
 * The two editorial gates on their own. Answers "what did the author ask for",
 * not "can a reader see it" - see `ArticleStatus` for why the distinction is
 * worth keeping.
 */
export function deriveArticleStatus(isVisible, publishAtUtc, now = new Date()) {
    if (!isVisible)
        return 'hidden';
    return toDate(publishAtUtc).getTime() > now.getTime() ? 'scheduled' : 'published';
}
/** The full three-gate verdict, matching kb.ArticleDetail.IsLive. */
export function deriveArticlePublishingStatus(gates, now = new Date()) {
    if (!gates.isVisible)
        return 'hidden';
    if (toDate(gates.publishAtUtc).getTime() > now.getTime())
        return 'scheduled';
    if (gates.currentRevisionNumber === null || gates.currentRevisionNumber === undefined) {
        // Nothing is current. Whether anything was EVER written is what separates a
        // withdrawn text from an article nobody has started - see the type.
        return typeof gates.latestRevisionNumber === 'number' ? 'withdrawn' : 'unwritten';
    }
    return 'live';
}
export function isArticleLive(gates, now = new Date()) {
    return deriveArticlePublishingStatus(gates, now) === 'live';
}
/**
 * Short label and the reason behind it, for badges and tooltips.
 *
 * `noun` is what the thing is called where a sentence names it - 'article' by
 * default, 'release note' for a release note (S42, 2026-10-12), whose states are
 * the article's because its publishing rule is the article's. Lower case, since
 * it reads mid-sentence. The default keeps every article string exactly as it was.
 */
export function describeArticlePublishingStatus(status, noun = 'article') {
    switch (status) {
        case 'live':
            return {
                label: 'Live',
                detail: 'Visible to everyone.',
            };
        case 'scheduled':
            return {
                label: 'Scheduled',
                detail: 'The publish date and time is in the future.',
            };
        case 'hidden':
            return {
                label: 'Hidden',
                detail: 'Hidden from readers, whatever the publish date says.',
            };
        case 'withdrawn':
            return {
                label: 'Withdrawn',
                detail: 'The published text was withdrawn. The history is intact, and nothing is served ' +
                    'until a revision is promoted.',
            };
        case 'unwritten':
            return {
                label: 'Unwritten',
                detail: `Nothing has been written yet: the ${noun} has no revision at all.`,
            };
        default: {
            const exhaustive = status;
            return { label: String(exhaustive), detail: '' };
        }
    }
}
/**
 * Label and reason for the two editorial gates on their own. `noun` as for
 * describeArticlePublishingStatus: 'article' by default, every article string
 * unchanged.
 */
export function describeArticleStatus(status, noun = 'article') {
    switch (status) {
        case 'published':
            return { label: 'Published', detail: `The publish date has passed and the ${noun} is visible.` };
        case 'scheduled':
            return { label: 'Scheduled', detail: 'Will become readable at the publish date and time.' };
        case 'hidden':
            return { label: 'Hidden', detail: 'Hidden from everyone except administrators.' };
        default: {
            const exhaustive = status;
            return { label: String(exhaustive), detail: '' };
        }
    }
}
function toDate(value) {
    return value instanceof Date ? value : new Date(value);
}
/**
 * Formats an instant for a `datetime-local` input, in the browser's timezone.
 * Scheduling is entered locally and stored as UTC, which is what
 * kb.Article.PublishAtUtc requires; the admin UI shows both so there is no doubt
 * which one is on screen.
 */
export function toLocalDateTimeInputValue(value) {
    const date = toDate(value);
    if (Number.isNaN(date.getTime()))
        return '';
    const pad = (part) => String(part).padStart(2, '0');
    return (`${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}`);
}
/** Converts a `datetime-local` value, read as local time, to an ISO UTC string. */
export function fromLocalDateTimeInputValue(value) {
    if (!value)
        return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime()))
        return null;
    return date.toISOString();
}
//# sourceMappingURL=publishing.js.map